import React, { useEffect, useMemo, useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';
import { useAuth } from '../../../contexts/AuthContext';
import { useToast } from '../../../contexts/ToastContext';
import UsageAnalytics from './UsageAnalytics';

// REST API base url and helper
const API_BASE_URL = import.meta.env?.VITE_API_BASE_URL;

async function apiFetch(path, options = {}) {
  const token = localStorage?.getItem('auth_token');
  const headers = {
    'Accept': 'application/json',
    ...(options?.body ? { 'Content-Type': 'application/json' } : {}),
    ...(token ? { 'Authorization': `Bearer ${token}` } : {})
  };

  const res = await fetch(`${API_BASE_URL}${path}`, { ...options, headers });
  const text = await res.text();
  let body = null;
  try { body = text ? JSON.parse(text) : null; } catch (_) {}
  if (!res.ok) {
    const err = new Error(body?.error || body?.message || `Request failed (${res.status})`);
    err.status = res.status;
    throw err;
  }
  return body;
}

// Shared default settings used when no settings exist yet for a project
const DEFAULT_SETTINGS = {
  cors_enabled: false,
  cors_allowed_origins_text: '',
  webhook_url: '',
  rate_limit_rpm: 600,
  ip_allowlist_text: '',
  logging_enabled: false,
  sandbox_mode: false,
  data_retention_days: 30,
  webhook_secret: '',
  env_name: '',
  env_custom_json_text: '',
  // Advanced CORS
  cors_allowed_methods_text: '',
  cors_allowed_headers_text: '',
  cors_exposed_headers_text: '',
  cors_max_age: 600,
  cors_allow_credentials: false,
  // Webhook delivery
  webhook_timeout_ms: 10000,
  webhook_retry_enabled: false,
  webhook_max_retries: 5,
  webhook_retry_backoff_ms: 1000,
  webhook_signature_algo: 'sha256',
  // Pagination & concurrency
  pagination_default_limit: 50,
  pagination_max_limit: 1000,
  concurrency_limit: 100,
  // Alerts & domain & auth
  alerts_enabled: false,
  alerts_email: '',
  custom_domain: '',
  jwt_audience: '',
  jwt_ttl_seconds: 3600
};

const ProjectManager = () => {
  const { user } = useAuth();
  const { add: addToast } = useToast();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [newProjectDescription, setNewProjectDescription] = useState('');
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [configOpen, setConfigOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settings, setSettings] = useState({ ...DEFAULT_SETTINGS });
  const [analyticsOpen, setAnalyticsOpen] = useState(false);
  const [selectedProjectForAnalytics, setSelectedProjectForAnalytics] = useState(null);

  const canLoad = useMemo(() => !!user?.id, [user?.id]);

  const loadProjects = async () => {
    if (!canLoad) return;
    setLoading(true);
    try {
      const data = await apiFetch('/projects', { method: 'GET' });
      // Support both new shape { items, total } and legacy array response
      const items = Array.isArray(data?.items)
        ? data.items
        : Array.isArray(data)
        ? data
        : [];
      setProjects(items);
    } catch (e) {
      console.error('Failed to load projects', e);
      setProjects([]);
      alert(e?.message || 'Failed to load projects');
    }
    setLoading(false);
  };

  const [statusUpdatingId, setStatusUpdatingId] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmConfig, setConfirmConfig] = useState({ title: '', message: '', onConfirm: null });

  const proceedUpdateStatus = async (project, nextStatus) => {
    if (!project?.id || !user?.id) return;
    setStatusUpdatingId(project.id);
    try {
      await apiFetch(`/projects/${project.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ status: nextStatus })
      });
      await loadProjects();
    } catch (e) {
      console.error('Failed to update project status', e);
      alert(e?.message || 'Failed to update project status');
    } finally {
      setStatusUpdatingId(null);
    }
  };

  const toggleProjectStatus = (project) => {
    if (!project) return;
    const nextStatus = project?.status === 'inactive' ? 'active' : 'inactive';
    if (nextStatus === 'inactive') {
      setConfirmConfig({
        title: 'Deactivate Project',
        message: `Are you sure you want to deactivate "${project?.title || project?.name}"? API access for this project may be blocked until reactivated.`,
        onConfirm: () => proceedUpdateStatus(project, nextStatus)
      });
      setConfirmOpen(true);
      return;
    }
    // Activating does not require confirm
    proceedUpdateStatus(project, nextStatus);
  };

  useEffect(() => {
    loadProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canLoad]);

  const openAnalytics = (project) => {
    setSelectedProjectForAnalytics(project);
    setAnalyticsOpen(true);
  };

  const openConfigure = async (project) => {
    setSelectedProject(project);
    setConfigOpen(true);
    // Ensure modal opens with empty defaults before fetch completes
    setSettings({ ...DEFAULT_SETTINGS });
    if (!project?.id || !user?.id) return;
    setSettingsLoading(true);
    try {
      const data = await apiFetch(`/projects/${project.id}/settings`, { method: 'GET' });
      if (data) {
        setSettings({
          cors_enabled: !!data.cors_enabled,
          cors_allowed_origins_text: Array.isArray(data.cors_allowed_origins) ? data.cors_allowed_origins.join(', ') : '',
          webhook_url: data.webhook_url || '',
          rate_limit_rpm: Number.isFinite(data.rate_limit_rpm) ? data.rate_limit_rpm : 600,
          ip_allowlist_text: Array.isArray(data.ip_allowlist) ? data.ip_allowlist.join(', ') : '',
          logging_enabled: data.logging_enabled ?? true,
          sandbox_mode: data.sandbox_mode ?? false,
          data_retention_days: Number.isFinite(data.data_retention_days) ? data.data_retention_days : 30,
          webhook_secret: data.webhook_secret || '',
          env_name: (data.environment && (data.environment.name || data.environment.env || '')) || '',
          env_custom_json_text: data.environment ? JSON.stringify(data.environment, null, 2) : '',
          // Advanced CORS
          cors_allowed_methods_text: Array.isArray(data.cors_allowed_methods) ? data.cors_allowed_methods.join(', ') : '',
          cors_allowed_headers_text: Array.isArray(data.cors_allowed_headers) ? data.cors_allowed_headers.join(', ') : '',
          cors_exposed_headers_text: Array.isArray(data.cors_exposed_headers) ? data.cors_exposed_headers.join(', ') : '',
          cors_max_age: Number.isFinite(data.cors_max_age) ? data.cors_max_age : 600,
          cors_allow_credentials: !!data.cors_allow_credentials,
          // Webhook delivery
          webhook_timeout_ms: Number.isFinite(data.webhook_timeout_ms) ? data.webhook_timeout_ms : 10000,
          webhook_retry_enabled: data.webhook_retry_enabled ?? true,
          webhook_max_retries: Number.isFinite(data.webhook_max_retries) ? data.webhook_max_retries : 5,
          webhook_retry_backoff_ms: Number.isFinite(data.webhook_retry_backoff_ms) ? data.webhook_retry_backoff_ms : 1000,
          webhook_signature_algo: data.webhook_signature_algo || 'sha256',
          // Pagination & concurrency
          pagination_default_limit: Number.isFinite(data.pagination_default_limit) ? data.pagination_default_limit : 50,
          pagination_max_limit: Number.isFinite(data.pagination_max_limit) ? data.pagination_max_limit : 1000,
          concurrency_limit: Number.isFinite(data.concurrency_limit) ? data.concurrency_limit : 100,
          // Alerts & domain & auth
          alerts_enabled: data.alerts_enabled ?? true,
          alerts_email: data.alerts_email || '',
          custom_domain: data.custom_domain || '',
          jwt_audience: data.jwt_audience || '',
          jwt_ttl_seconds: Number.isFinite(data.jwt_ttl_seconds) ? data.jwt_ttl_seconds : 3600
        });
      } else {
        // Defaults for new
        setSettings({ ...DEFAULT_SETTINGS });
      }
    } catch (e) {
      if (e?.status === 404) {
        // Defaults for not-found settings
        setSettings({ ...DEFAULT_SETTINGS });
      } else {
        console.error('Load project settings failed', e);
        alert(e?.message || 'Failed to load project settings');
      }
    } finally {
      setSettingsLoading(false);
    }
  };

  const saveSettings = async () => {
    if (!selectedProject?.id || !user?.id) return;
    
    // Validate alerts settings
    // if (settings.alerts_enabled && !settings.alerts_email.trim()) {
    //   alert('Email address is required when alerts are enabled.');
    //   return;
    // }
    
    setSettingsSaving(true);
    try {
      const origins = settings.cors_allowed_origins_text
        .split(',')
        .map(s => s.trim())
        .filter(Boolean);
      const ipAllow = settings.ip_allowlist_text
        .split(',')
        .map(s => s.trim())
        .filter(Boolean);
      const corsAllowedMethods = settings.cors_allowed_methods_text.split(',').map(s => s.trim()).filter(Boolean);
      const corsAllowedHeaders = settings.cors_allowed_headers_text.split(',').map(s => s.trim()).filter(Boolean);
      const corsExposedHeaders = settings.cors_exposed_headers_text.split(',').map(s => s.trim()).filter(Boolean);

      // Build environment json
      let environment = {};
      if (settings.env_custom_json_text?.trim()) {
        try {
          environment = JSON.parse(settings.env_custom_json_text);
        } catch (e) {
          alert('Environment JSON is invalid. Please correct it.');
          setSettingsSaving(false);
          return;
        }
      }
      if (settings.env_name?.trim()) {
        environment = { name: settings.env_name.trim(), ...environment };
      }

      await apiFetch(`/projects/${selectedProject.id}/settings`, {
        method: 'PUT',
        body: JSON.stringify({
          cors_enabled: !!settings.cors_enabled,
          cors_allowed_origins: origins,
          webhook_url: settings.webhook_url || null,
          rate_limit_rpm: parseInt(settings.rate_limit_rpm || 0, 10) || 0,
          ip_allowlist: ipAllow,
          logging_enabled: !!settings.logging_enabled,
          sandbox_mode: !!settings.sandbox_mode,
          data_retention_days: parseInt(settings.data_retention_days || 0, 10) || 0,
          webhook_secret: settings.webhook_secret || null,
          environment,
          // Advanced CORS
          cors_allowed_methods: corsAllowedMethods,
          cors_allowed_headers: corsAllowedHeaders,
          cors_exposed_headers: corsExposedHeaders,
          cors_max_age: parseInt(settings.cors_max_age || 0, 10) || 0,
          cors_allow_credentials: !!settings.cors_allow_credentials,
          // Webhook delivery
          webhook_timeout_ms: parseInt(settings.webhook_timeout_ms || 0, 10) || 0,
          webhook_retry_enabled: !!settings.webhook_retry_enabled,
          webhook_max_retries: parseInt(settings.webhook_max_retries || 0, 10) || 0,
          webhook_retry_backoff_ms: parseInt(settings.webhook_retry_backoff_ms || 0, 10) || 0,
          webhook_signature_algo: settings.webhook_signature_algo || 'sha256',
          // Pagination & concurrency
          pagination_default_limit: parseInt(settings.pagination_default_limit || 0, 10) || 0,
          pagination_max_limit: parseInt(settings.pagination_max_limit || 0, 10) || 0,
          concurrency_limit: parseInt(settings.concurrency_limit || 0, 10) || 0,
          // Alerts & domain & auth
          alerts_enabled: !!settings.alerts_enabled,
          alerts_email: settings.alerts_email || null,
          custom_domain: settings.custom_domain || null,
          jwt_audience: settings.jwt_audience || null,
          jwt_ttl_seconds: parseInt(settings.jwt_ttl_seconds || 0, 10) || 0
        })
      });
      setConfigOpen(false);
    } catch (e) {
      console.error('Save project settings failed', e);
      alert(e?.message || 'Failed to save project settings');
    } finally {
      setSettingsSaving(false);
    }
  };

  const handleCreateProject = async () => {
    if (!newProjectName?.trim() || !user?.id) return;
    setCreating(true);
    try {
      await apiFetch('/projects', {
        method: 'POST',
        body: JSON.stringify({
          name: newProjectName.trim(),
          description: newProjectDescription?.trim() || '',
          status: 'active'
        })
      });
      setNewProjectName('');
      setNewProjectDescription('');
      setShowCreateForm(false);
      await loadProjects();
      addToast({
        title: 'Project created',
        description: 'Your project has been created successfully.',
        variant: 'success'
      });
    } catch (e) {
      console.error('Failed to create project', e);
      alert(e?.message || 'Failed to create project');
    } finally {
      setCreating(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-accent/10 text-accent border-accent/20';
      case 'development':
        return 'bg-warning/10 text-warning border-warning/20';
      case 'inactive':
        return 'bg-muted text-muted-foreground border-border';
      default:
        return 'bg-muted text-muted-foreground border-border';
    }
  };


  return (
    <>
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-foreground">Projects</h2>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            iconName="RefreshCw"
            iconPosition="left"
            onClick={() => loadProjects()}
            disabled={loading}
          >
            {loading ? 'Refreshing...' : 'Refresh'}
          </Button>
          <Button
            variant="default"
            size="sm"
            iconName="Plus"
            iconPosition="left"
            onClick={() => setShowCreateForm(!showCreateForm)}
          >
            New Project
          </Button>
        </div>
      </div>
      {showCreateForm && (
        <div className="mb-6 p-4 bg-muted/50 rounded-lg border border-border">
          <h3 className="text-sm font-medium text-foreground mb-3">Create New Project</h3>
          <div className="space-y-3">
            <Input
              placeholder="Project name"
              value={newProjectName}
              onChange={(e) => setNewProjectName(e?.target?.value)}
            />
            <Input
              placeholder="Description (optional)"
              value={newProjectDescription}
              onChange={(e) => setNewProjectDescription(e?.target?.value)}
            />
            <div className="flex gap-3">
              <Button
                variant="default"
                size="sm"
                onClick={handleCreateProject}
                disabled={!newProjectName?.trim() || creating}
              >
                {creating ? 'Creating...' : 'Create Project'}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowCreateForm(false)}
                disabled={creating}
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {loading ? (
          <div className="text-sm text-muted-foreground">Loading projects...</div>
        ) : projects?.length === 0 ? (
          <div className="text-sm text-muted-foreground">No projects yet. Create your first project.</div>
        ) : (
          projects?.map((project) => (
            <div key={project?.id} className="p-4 bg-muted/30 rounded-lg border border-border hover:bg-muted/50 transition-colors">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <h3 className="font-medium text-foreground mb-1">{project?.title || project?.name}</h3>
                  {project?.description && (
                    <p className="text-sm text-muted-foreground">{project?.description}</p>
                  )}
                </div>
                <div className="flex items-center space-x-2 ml-4">
                  <div className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(project?.status)}`}>
                    {project?.status || 'active'}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
                <div>
                  <div className="flex items-center space-x-2 mb-1">
                    <Icon name="Clock" size={14} className="text-warning" />
                    <span className="text-muted-foreground">Created</span>
                  </div>
                  <p className="font-medium text-foreground">{project?.created_at ? new Date(project?.created_at).toLocaleString() : '-'}</p>
                </div>
                <div>
                  <div className="flex items-center space-x-2 mb-1">
                    <Icon name="Clock" size={14} className="text-secondary" />
                    <span className="text-muted-foreground">Updated</span>
                  </div>
                  <p className="font-medium text-foreground">{project?.updated_at ? new Date(project?.updated_at).toLocaleString() : '-'}</p>
                </div>
              </div>

              <div className="flex items-center justify-between pt-3 border-t border-border">
                <div className="flex items-center gap-2">
                  <Button
                    variant={project?.status === 'inactive' ? 'default' : 'outline'}
                    size="sm"
                    iconName={project?.status === 'inactive' ? 'Play' : 'Pause'}
                    iconPosition="left"
                    onClick={() => toggleProjectStatus(project)}
                    disabled={statusUpdatingId === project?.id}
                    className="text-xs"
                  >
                    {statusUpdatingId === project?.id
                      ? 'Updating...'
                      : project?.status === 'inactive' ? 'Activate' : 'Deactivate'}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="Settings"
                    iconPosition="left"
                    className="text-xs"
                    onClick={() => openConfigure(project)}
                  >
                    Configure
                  </Button>
                </div>
                <div className="flex items-center space-x-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="BarChart3"
                    iconPosition="left"
                    className="text-xs"
                    onClick={() => openAnalytics(project)}
                  >
                    Analytics
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                  >
                    <Icon name="MoreHorizontal" size={14} />
                  </Button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>

    {/* Configure Project Modal */}
    <Modal
      open={configOpen}
      title={`Configure Project${(selectedProject?.title || selectedProject?.name) ? `: ${selectedProject?.title || selectedProject?.name}` : ''}`}
      onClose={() => setConfigOpen(false)}
      className="max-w-2xl"
    >
      {settingsLoading ? (
        <div className="text-sm text-muted-foreground">Loading settings...</div>
      ) : (
        <div className="space-y-6">
          {/* Webhook Configuration */}
          <div className="space-y-4">
            <h4 className="text-sm font-semibold text-foreground border-b border-border pb-2">Webhook Configuration</h4>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Webhook URL</label>
              <Input
                placeholder="https://your-service.example.com/webhooks/messaginghub"
                value={settings.webhook_url}
                onChange={(e) => setSettings(s => ({ ...s, webhook_url: e?.target?.value }))}
                disabled
              />
              <p className="text-xs text-muted-foreground mt-1">We will POST event payloads to this URL if configured.</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Webhook Secret</label>
              <Input
                placeholder="Used to sign webhook requests"
                value={settings.webhook_secret}
                onChange={(e) => setSettings(s => ({ ...s, webhook_secret: e?.target?.value }))}
                disabled
              />
              <p className="text-xs text-muted-foreground mt-1">If provided, requests will include a signature header.</p>
            </div>
          </div>

          {/* CORS Configuration */}
          <div className="space-y-4">
            <h4 className="text-sm font-semibold text-foreground border-b border-border pb-2">CORS Configuration</h4>
            <div className="flex items-center gap-2">
              <Checkbox
                checked={!!settings.cors_enabled}
                onChange={(e) => setSettings(s => ({ ...s, cors_enabled: e?.target?.checked }))}
                label="Enable CORS"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Allowed Origins</label>
              <Input
                placeholder="e.g. https://app.example.com, http://localhost:5173"
                value={settings.cors_allowed_origins_text}
                onChange={(e) => setSettings(s => ({ ...s, cors_allowed_origins_text: e?.target?.value }))}
              />
              <p className="text-xs text-muted-foreground mt-1">Comma-separated list of origins. Leave blank to disallow all (unless CORS is disabled).</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Allowed Methods</label>
              <Input
                placeholder="e.g. GET, POST, PUT, DELETE, OPTIONS"
                value={settings.cors_allowed_methods_text}
                onChange={(e) => setSettings(s => ({ ...s, cors_allowed_methods_text: e?.target?.value }))}
              />
              <p className="text-xs text-muted-foreground mt-1">Comma-separated list of HTTP methods. Leave blank for default methods.</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Allowed Headers</label>
              <Input
                placeholder="e.g. Content-Type, Authorization, X-Requested-With"
                value={settings.cors_allowed_headers_text}
                onChange={(e) => setSettings(s => ({ ...s, cors_allowed_headers_text: e?.target?.value }))}
              />
              <p className="text-xs text-muted-foreground mt-1">Comma-separated list of allowed headers. Leave blank for default headers.</p>
            </div>
          </div>

          {/* Security & Access */}
          <div className="space-y-4">
            <h4 className="text-sm font-semibold text-foreground border-b border-border pb-2">Security & Access</h4>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">IP Allowlist</label>
              <Input
                placeholder="e.g. 203.0.113.1, 2001:db8::1"
                value={settings.ip_allowlist_text}
                onChange={(e) => setSettings(s => ({ ...s, ip_allowlist_text: e?.target?.value }))}
              />
              <p className="text-xs text-muted-foreground mt-1">Comma-separated IPv4/IPv6 addresses. Leave blank to allow all.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Rate Limit (requests per minute)</label>
                <Input
                  type="number"
                  min={0}
                  value={settings.rate_limit_rpm}
                  onChange={(e) => setSettings(s => ({ ...s, rate_limit_rpm: parseInt(e?.target?.value || '0', 10) }))}
                />
                <p className="text-xs text-muted-foreground mt-1">Set to 0 for unlimited.</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Data Retention (days)</label>
                <Input
                  type="number"
                  min={0}
                  value={settings.data_retention_days}
                  onChange={(e) => setSettings(s => ({ ...s, data_retention_days: parseInt(e?.target?.value || '0', 10) }))}
                />
                <p className="text-xs text-muted-foreground mt-1">How long to retain logs/data for this project.</p>
              </div>
            </div>
          </div>

          {/* Notifications */}
          <div className="space-y-4">
            <h4 className="text-sm font-semibold text-foreground border-b border-border pb-2">Notifications</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center gap-2">
                <Checkbox
                  checked={!!settings.alerts_enabled}
                  onChange={(e) => {
                    const enabled = e?.target?.checked;
                    // Only allow enabling if email is provided
                    if (enabled && !settings.alerts_email.trim()) {
                      alert('Please enter an email address before enabling alerts.');
                      return;
                    }
                    setSettings(s => ({ ...s, alerts_enabled: enabled }));
                  }}
                  label="Enable Alerts"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Alerts Email</label>
                <Input
                  type="email"
                  placeholder="alerts@example.com"
                  value={settings.alerts_email}
                  onChange={(e) => {
                    const email = e?.target?.value;
                    setSettings(s => ({ 
                      ...s, 
                      alerts_email: email
                      // Don't auto-enable alerts - let user manually check
                    }));
                  }}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  <span className="text-blue-500">💡 Tip: If enabled without specifying an email, alerts will be sent to the tenant owner's email address.</span>
                </p>
              </div>
            </div>
          </div>

          {/* Environment & Logging */}
          <div className="space-y-4">
            <h4 className="text-sm font-semibold text-foreground border-b border-border pb-2">Environment & Logging</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center gap-2">
                <Checkbox
                  checked={!!settings.logging_enabled}
                  onChange={(e) => setSettings(s => ({ ...s, logging_enabled: e?.target?.checked }))}
                  label="Enable Logging"
                  disabled
                />
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  checked={!!settings.sandbox_mode}
                  onChange={(e) => setSettings(s => ({ ...s, sandbox_mode: e?.target?.checked }))}
                  label="Sandbox Mode"
                  disabled
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Environment Name</label>
                <Input
                  placeholder="e.g. production, staging"
                  value={settings.env_name}
                  onChange={(e) => setSettings(s => ({ ...s, env_name: e?.target?.value }))}
                  disabled
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Environment JSON (optional)</label>
                <textarea
                  className="w-full h-28 rounded-md border border-border bg-background p-2 text-sm font-mono"
                  placeholder='{ "region": "us-east-1" }'
                  value={settings.env_custom_json_text}
                  onChange={(e) => setSettings(s => ({ ...s, env_custom_json_text: e?.target?.value }))}
                  disabled
                />
                <p className="text-xs text-muted-foreground mt-1">Additional environment metadata in JSON format.</p>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 pt-2">
            <Button variant="ghost" size="sm" onClick={() => setConfigOpen(false)} disabled={settingsSaving}>Cancel</Button>
            <Button variant="default" size="sm" onClick={saveSettings} disabled={settingsSaving}>
              {settingsSaving ? 'Saving...' : 'Save Settings'}
            </Button>
          </div>
        </div>
      )}
    </Modal>

    {/* Analytics Modal */}
    <Modal
      open={analyticsOpen}
      title={`Analytics - ${(selectedProjectForAnalytics?.title || selectedProjectForAnalytics?.name) || 'Project'}`}
      onClose={() => setAnalyticsOpen(false)}
      className="max-w-6xl"
    >
      <UsageAnalytics projectId={selectedProjectForAnalytics?.id} />
    </Modal>

    {/* Confirm Modal */}
    <Modal
      open={confirmOpen}
      title={confirmConfig.title || 'Please Confirm'}
      onClose={() => setConfirmOpen(false)}
      className="max-w-md"
    >
      <div className="space-y-4">
        <p className="text-sm text-foreground">{confirmConfig.message}</p>
        <div className="flex items-center justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button
            variant="destructive"
            size="sm"
            onClick={() => { setConfirmOpen(false); confirmConfig.onConfirm?.(); }}
          >
            Confirm
          </Button>
        </div>
      </div>
    </Modal>
    </>
  );
};

// Simple modal component inside this file for convenience
const Modal = ({ open, title, children, onClose, className = "" }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose}></div>
      <div className={`relative bg-card border border-border rounded-lg shadow-elevation-2 w-full mx-4 ${className}`}>
        <div className="px-4 py-3 border-b border-border flex items-center justify-between">
          <h3 className="text-base font-semibold text-foreground">{title}</h3>
          <button className="text-muted-foreground hover:text-foreground" onClick={onClose}>
            ×
          </button>
        </div>
        <div className="p-4 max-h-[80vh] overflow-y-auto">
          {children}
        </div>
      </div>
    </div>
  );
};

export default ProjectManager;